import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, Download } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";
import { downloadCustomerStatementPDF } from "@/lib/pdf";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  usePageTitle(data?.customer ? data.customer.name : "Customer");
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [range, setRange] = useState({ from: firstOfMonth, to: today });
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setData(r.data));
  }, [id]);

  const downloadStatement = async () => {
    setErr("");
    setDownloading(true);
    try {
      const { data: st } = await api.get(`/customers/${id}/statement`, {
        params: { date_from: range.from, date_to: range.to },
      });
      await downloadCustomerStatementPDF({
        customer: st.customer,
        orders: st.orders,
        payments: st.payments,
        totals: st.totals,
        dateFrom: st.date_from,
        dateTo: st.date_to,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to fetch statement");
    } finally { setDownloading(false); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { customer, orders } = data;

  return (
    <div className="space-y-6" data-testid="customer-detail">
      <Link to="/customers" className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Customers
      </Link>
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">{customer.company || "Individual"}</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">{customer.name}</h1>
      </div>

      {/* Monthly statement PDF */}
      <div className="card-swiss p-5 space-y-3" data-testid="statement-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Customer Statement</div>
            <div className="text-sm text-muted-foreground mt-1">One-click PDF of all orders, payments and outstanding for a period.</div>
          </div>
          <Download size={20} className="text-[#FF6B00]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div>
            <label className="overline block mb-1">From</label>
            <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
              data-testid="statement-from-input" />
          </div>
          <div>
            <label className="overline block mb-1">To</label>
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
              data-testid="statement-to-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ["This Month", firstOfMonth, today],
              ["Last 30d", new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), today],
              ["This Year", today.slice(0, 4) + "-01-01", today],
            ].map(([lbl, f, t]) => (
              <button key={lbl} type="button" onClick={() => setRange({ from: f, to: t })}
                className="text-xs border border-[hsl(var(--border))] hover:border-[#FF6B00] px-2 py-1"
                data-testid={`preset-${lbl.toLowerCase().replace(/\s+/g, "-")}`}>{lbl}</button>
            ))}
          </div>
          <button onClick={downloadStatement} disabled={downloading}
            className="btn-swiss btn-accent justify-center" data-testid="download-statement-btn">
            <Download size={14} /> {downloading ? "Building…" : "Download Statement"}
          </button>
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ["Phone", customer.phone],
          ["Email", customer.email || "—"],
          ["GST", customer.gst || "—"],
          ["WhatsApp", customer.whatsapp || "—"],
          ["Address", customer.address || "—"],
          ["Outstanding", `₹${Math.round(customer.outstanding || 0).toLocaleString("en-IN")}`],
        ].map(([k, v]) => (
          <div key={k} className="card-swiss p-4">
            <div className="overline">{k}</div>
            <div className="mt-2 text-sm">{v}</div>
          </div>
        ))}
      </div>

      <div className="card-swiss">
        <div className="p-5 border-b border-[hsl(var(--border))] overline">
          Orders ({orders.length})
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-5 py-3 text-left overline">Order</th>
              <th className="px-5 py-3 text-left overline">Product</th>
              <th className="px-5 py-3 text-right overline">Amount</th>
              <th className="px-5 py-3 text-left overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                <td className="px-5 py-3">
                  <Link to={`/orders/${o.id}`} className="font-mono text-xs hover:text-[#FF6B00]">
                    {o.id}
                  </Link>
                </td>
                <td className="px-5 py-3">{o.product_name}</td>
                <td className="px-5 py-3 text-right font-mono">
                  ₹{Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}
                </td>
                <td className="px-5 py-3">
                  <StatusBadge status={o.status} />
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-muted-foreground">
                  No orders yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
